import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  AlertBox,
  Button,
  InputGroup,
  InputSelect,
  ModalInfo,
  ModalInfoProps,
  OptionExtendChain,
} from '@/components';
import { COMMON_CONTAINER_ID } from '@/config';
import { AmlFlowEnum, UpdateCheckModeRequest } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { profileSelectors, userSelectors } from '@/redux/selectors';
import { blockActions, userActions } from '@/redux/actions';
import { CheckModeType, Validation } from '@/models';
import { useInputText } from '@/hooks';

import styles from './CheckMethodModal.module.sass';

type OptionType = {
  value: CheckModeType;
  label: string;
  description: string;
};

type CheckMethodModalProps = ModalInfoProps;

const CheckMethodModal: FC<CheckMethodModalProps> = (props) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useAppSelector(userSelectors.getUserSlice);
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );

  const isAccountLiteMode = useAppSelector(profileSelectors.isAccountLiteMode);

  const presetedFlowModes = useAppSelector(profileSelectors.presetedFlowModes);

  const options: OptionType[] = useMemo(() => {
    let allowedModes: CheckModeType[] = Object.values(AmlFlowEnum);

    if (!profileData?.isAccurateAllowed) {
      allowedModes = allowedModes.filter(
        (flow) => flow !== AmlFlowEnum.ACCURATE,
      );
    }

    if (Array.isArray(presetedFlowModes) && presetedFlowModes.length > 0) {
      allowedModes = allowedModes.filter((flow) =>
        presetedFlowModes.includes(flow),
      );
    }

    return allowedModes.map((flow) => {
      const option: OptionType = {
        value: flow,
        label: t(`translation.${flow}_flow_str`),
        description: t(`translation.amlflow_${flow}_str_option`),
      };

      return option;
    });
  }, [
    profileData?.isAccurateAllowed,
    profileData?.preferences,
    isAccountLiteMode,
    profileData?.accountMode,
    t,
  ]);

  const CHECK_MODE_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );
  const checkModeField = useInputText(
    userData?.amlFlow || '',
    CHECK_MODE_VALIDATIONS,
  );

  useEffect(() => {
    if (isOpen) {
      checkModeField.onChange(userData?.amlFlow || '');
      dispatch(blockActions.block());
    } else {
      dispatch(blockActions.unblock());
    }
    dispatch(userActions.setError(null));

    return () => {
      dispatch(userActions.setError(null));
    };
  }, [isOpen]);

  const submitHandler = () => {
    if (!userLoading) {
      if (checkModeField.isValid) {
        const payload: UpdateCheckModeRequest = {
          flow: checkModeField.value as AmlFlowEnum,
        };

        dispatch(userActions.updateCheckMode(payload));
      } else if (!checkModeField.isValid) {
        checkModeField.onBlur();
      }
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.change_amlflow_title')}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            disabled={userLoading || isAccountLiteMode}
            isLoading={userLoading}
            onClick={submitHandler}
          >
            {t('translation.save_amlflow_link')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            onClick={onClose}
            disabled={userLoading}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <div className={styles.form}>
        {!!userError && (
          <AlertBox className={styles.error} type="danger">
            {{ description: userError }}
          </AlertBox>
        )}
        <InputGroup>
          <InputSelect
            id="flow"
            name="flow"
            classNamePrefix="chain-select"
            disabled={isAccountLiteMode}
            value={checkModeField.value}
            options={options}
            components={{ Option: OptionExtendChain }}
            onChange={checkModeField.onChange}
            onBlur={checkModeField.onBlur}
          />
        </InputGroup>
      </div>
    </ModalInfo>
  );
};

export default CheckMethodModal;
